import { useQuery } from '@apollo/client';
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import EmptyState from '../../components/EmptyState/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert/ErrorAlert';
import FilterChip from '../../components/FilterChip/FilterChip';
import Loader from '../../components/Loader/Loader';
import Page from '../../components/Page/Page';
import Badge from '../../components/Styled/Badge';
import TextInput from '../../components/TextInput/TextInput';
import {
  PagedSupportTickets,
  SupportTicketCounts,
} from '../../graphql/graphql';
import {
  SUPPORT_TICKETS,
  SUPPORT_TICKET_COUNTS,
} from '../../graphql/queries-gql';
import {
  categoryLabel,
  formatListTime,
  senderLabel,
  SOURCE_OPTIONS,
  sourceLabel,
  StatusFilter,
  statusMeta,
} from './supportMeta';
import TicketThread from './TicketThread';

const PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 60_000;
const ALL_SOURCES = 'all';

const STATUS_FILTERS: {
  value: StatusFilter;
  label: string;
  count?: keyof Omit<SupportTicketCounts, '__typename'>;
}[] = [
  { value: 'open', label: 'Open', count: 'open' },
  { value: 'snoozed', label: 'Snoozed', count: 'snoozed' },
  { value: 'waiting_user', label: 'Waiting on user', count: 'waitingUser' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'spam', label: 'Spam' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

// Two panes filling the <Page fill> body; each pane scrolls on its own.
// On narrow screens only one is shown: the list, or the open ticket.
const Split = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 16px;
  max-width: 100%;
`;

const Pane = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background-color: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
`;

const ListPane = styled(Pane)<{ $hiddenOnNarrow: boolean }>`
  width: 380px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
    display: ${(props) => (props.$hiddenOnNarrow ? 'none' : 'flex')};
  }
`;

const ThreadPane = styled(Pane)<{ $hiddenOnNarrow: boolean }>`
  flex: 1;

  @media (max-width: 900px) {
    display: ${(props) => (props.$hiddenOnNarrow ? 'none' : 'flex')};
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.borderStrong};
    border-radius: 3px;
  }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
`;

const Row = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 8px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  background-color: ${(props) =>
    props.$selected ? props.theme.surfaceActive : 'transparent'};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) =>
      props.$selected ? props.theme.surfaceActive : props.theme.surfaceHover};
  }
`;

const RowTop = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const UnansweredDot = styled.span`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.blue};
`;

const Sender = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
`;

const RowTime = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: ${(props) => props.theme.mutedTextColor};
  font-variant-numeric: tabular-nums;
`;

const RowSubject = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
`;

const RowPreview = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13px;
  line-height: 1.4;
  overflow-wrap: anywhere;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const RowBadges = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
`;

const ReportedContent = styled.span`
  font-size: 12px;
  overflow-wrap: anywhere;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const TicketNumber = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 11.5px;
  color: ${(props) => props.theme.mutedTextColor};
`;

const ListFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 6px 14px;
  border-top: 1px solid ${(props) => props.theme.border};
  font-size: 12.5px;
  color: ${(props) => props.theme.secondaryTextColor};
  font-variant-numeric: tabular-nums;
`;

const CenterFill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 24px;
`;

const SearchBox = styled.div`
  width: 280px;
  max-width: 100%;
`;

// The support desk: every ticket from email, the app and the in-app support
// chat, with the thread of the selected one next to the list.
const Support: React.FC = () => {
  const navigate = useNavigate();
  const { id: selectedId } = useParams<{ id: string }>();
  const [status, setStatus] = useState<StatusFilter>('open');
  const [needsAdmin, setNeedsAdmin] = useState(false);
  const [source, setSource] = useState(ALL_SOURCES);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  // A search looks through every status: the point is to find the ticket,
  // wherever it ended up.
  const searching = search.trim().length > 0;
  const filter = {
    status: searching ? 'all' : status,
    source: source === ALL_SOURCES ? undefined : source,
    needsAdmin: needsAdmin && !searching ? true : undefined,
    search: searching ? search.trim() : undefined,
  };

  useEffect(() => {
    setOffset(0);
  }, [status, needsAdmin, source, search]);

  const { data, loading, error, refetch } = useQuery<{
    supportTickets: PagedSupportTickets;
  }>(SUPPORT_TICKETS, {
    variables: { filter, limit: PAGE_SIZE, offset },
    fetchPolicy: 'cache-and-network',
    pollInterval: POLL_INTERVAL_MS,
  });

  const { data: countsData, refetch: refetchCounts } = useQuery<{
    supportTicketCounts: SupportTicketCounts;
  }>(SUPPORT_TICKET_COUNTS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: POLL_INTERVAL_MS,
  });

  const counts = countsData?.supportTicketCounts;
  const page = data?.supportTickets;
  const tickets = page?.nodes ?? [];

  const refresh = () => {
    refetch();
    refetchCounts();
  };

  return (
    <Page
      title="Support"
      description="Tickets from email, the app and the in-app support chat."
      badge={
        counts ? (
          <Badge $tone={counts.open > 0 ? 'blue' : 'neutral'}>
            {counts.open} open
          </Badge>
        ) : undefined
      }
      fill
      actions={
        <>
          <SearchBox>
            <TextInput
              placeHolder="Number, email, user id or subject"
              icon={<MagnifyingGlassIcon width={16} />}
              allowEmpty
              onSubmit={setSearch}
              onChange={(value) => {
                if (!value) setSearch('');
              }}
            />
          </SearchBox>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} aria-label="Refresh">
              <ArrowPathIcon width={18} />
            </IconButton>
          </Tooltip>
        </>
      }
      toolbar={
        <>
          {STATUS_FILTERS.map((option) => {
            const count = option.count && counts ? counts[option.count] : null;
            return (
              <FilterChip
                key={option.value}
                label={
                  count !== null ? `${option.label} ${count}` : option.label
                }
                selected={!searching && status === option.value}
                onChange={() => setStatus(option.value)}
              />
            );
          })}
          <FilterChip
            label={
              counts ? `Needs admin ${counts.needsAdmin}` : 'Needs admin'
            }
            title="Tickets an agent parked for a human"
            selected={!searching && needsAdmin}
            onChange={setNeedsAdmin}
          />
          <Select
            size="small"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            aria-label="Source"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value={ALL_SOURCES}>All sources</MenuItem>
            {SOURCE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {searching && <Badge>Searching every status</Badge>}
        </>
      }
    >
      <Split>
        <ListPane $hiddenOnNarrow={!!selectedId}>
          <ScrollArea>
            {error && (
              <CenterFill>
                <ErrorAlert error={error} />
              </CenterFill>
            )}
            {!error && !page && loading && (
              <CenterFill>
                <Loader />
              </CenterFill>
            )}
            {!error && page && tickets.length === 0 && (
              <CenterFill>
                <EmptyState
                  Icon={InboxIcon}
                  title={searching ? 'No tickets found' : 'Nothing here'}
                  description={
                    searching
                      ? 'Try a ticket number, the full email address or a word from the subject.'
                      : 'No tickets match these filters.'
                  }
                />
              </CenterFill>
            )}
            <Rows>
              {tickets.map((ticket) => {
                const meta = statusMeta(ticket);
                // Open with the user's message last: the ball is with us
                const unanswered =
                  ticket.status === 'open' &&
                  ticket.previewDirection !== 'outbound';
                return (
                  <Row
                    key={ticket._id}
                    type="button"
                    $selected={ticket._id === selectedId}
                    onClick={() => navigate(`/support/${ticket._id}`)}
                  >
                    <RowTop>
                      {unanswered && <UnansweredDot title="Needs a reply" />}
                      <Sender>{senderLabel(ticket)}</Sender>
                      <RowTime>{formatListTime(ticket.lastInboundAt)}</RowTime>
                    </RowTop>
                    <RowSubject>{ticket.subject || '(no subject)'}</RowSubject>
                    {ticket.report?.contentId && (
                      <ReportedContent>
                        Reported {ticket.report.type.toLowerCase()}:{' '}
                        <TicketNumber>{ticket.report.contentId}</TicketNumber>
                      </ReportedContent>
                    )}
                    {ticket.preview && (
                      <RowPreview>
                        {ticket.previewDirection === 'outbound' ? 'You: ' : ''}
                        {ticket.preview}
                      </RowPreview>
                    )}
                    <RowBadges>
                      <TicketNumber>{ticket.displayId}</TicketNumber>
                      <Badge $tone={meta.tone}>{meta.label}</Badge>
                      {ticket.needsAdmin && (
                        <Badge $tone="red">Needs admin</Badge>
                      )}
                      <Badge>{sourceLabel(ticket.source)}</Badge>
                      {ticket.category && (
                        <Badge>{categoryLabel(ticket.category)}</Badge>
                      )}
                      {ticket.language && (
                        <Badge>{ticket.language.toUpperCase()}</Badge>
                      )}
                      {ticket.user?.premium && (
                        <Badge $tone="orange">Premium</Badge>
                      )}
                      {ticket.hasAttachments && (
                        <Badge title="Has attachments">
                          <PaperClipIcon />
                        </Badge>
                      )}
                    </RowBadges>
                  </Row>
                );
              })}
            </Rows>
          </ScrollArea>
          {page && page.total > 0 && (
            <ListFooter>
              <span>
                {page.offset + 1} to {page.offset + tickets.length} of{' '}
                {page.total}
              </span>
              <span>
                <IconButton
                  size="small"
                  disabled={page.offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon width={16} />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!page.hasNextPage}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  aria-label="Next page"
                >
                  <ChevronRightIcon width={16} />
                </IconButton>
              </span>
            </ListFooter>
          )}
        </ListPane>

        <ThreadPane $hiddenOnNarrow={!selectedId}>
          {selectedId ? (
            // Keyed so the draft and the open menus reset with the ticket
            <TicketThread
              key={selectedId}
              ticketId={selectedId}
              onBack={() => navigate('/support')}
            />
          ) : (
            <CenterFill>
              <EmptyState
                Icon={InboxIcon}
                title="No ticket selected"
                description="Pick a ticket to read the conversation, reply, resolve or reopen it."
              />
            </CenterFill>
          )}
        </ThreadPane>
      </Split>
    </Page>
  );
};
export default Support;
